"use client";

import React from "react";
import { ArrowRight, ArrowUpRight, ShieldCheck, Wrench, Users } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const services = [{
  id: "commercial",
  title: "Commercial Scaffolding",
  description: "Multi-level access towers, perimeter systems, and structural containment for main contractors and major site developments.",
  to: "/CommercialScaffoldingPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Commercial-Scaffolding.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Commercial-Scaffolding.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Commercial-Scaffolding.png-wvc-sizes",
  alt: "Multi-level commercial scaffolding structure erected alongside building development",
  specTag: "TG20:21 Compliant",
  photoName: "Homepage Commercial Service Image"
}, {
  id: "residential",
  title: "Residential Scaffolding",
  description: "Safe, tidy domestic access solutions for extensions, exterior decorating, chimneys, and structural renovations.",
  to: "/ResidentialScaffoldingPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Residnetial-scaffolding.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Residnetial-scaffolding.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Residnetial-scaffolding.png-wvc-sizes",
  alt: "Domestic residential scaffolding with working platforms and guardrails on a home exterior",
  specTag: "Homeowner Friendly",
  photoName: "Homepage Residential Service Image"
}, {
  id: "roofing",
  title: "Roofing Scaffolding",
  description: "Temporary roof protection, edge barriers, and chimney saddles engineered for safe high-level work in all weather conditions.",
  to: "/RoofingScaffoldingPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Roofing-scaffoling.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Roofing-scaffoling.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Roofing-scaffoling.png-wvc-sizes",
  alt: "Scaffolding structure providing roof-level access and edge protection around a roofline",
  specTag: "Edge Protection",
  photoName: "Homepage Roofing Service Image"
}, {
  id: "solar",
  title: "Solar Scaffolding",
  description: "Fast, dependable staging designed specifically for domestic and commercial solar panel installations across the South East.",
  to: "/SolarScaffoldingPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Solar-panel-1-1.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Solar-panel-1-1.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/Solar-panel-1-1.png-wvc-sizes",
  alt: "Roof-level access scaffold installed for solar panel fitting and maintenance",
  specTag: "MCS Installer Ready",
  photoName: "Homepage Solar Service Image"
}];
const specialistServices = [{
  name: "Temporary Roofs",
  to: "/TemporaryRoofsPage"
}, {
  name: "New Build Scaffolding",
  to: "/NewBuildScaffoldingPage"
}, {
  name: "Refurbishment Scaffolding",
  to: "/RefurbishmentScaffoldingPage"
}, {
  name: "Industrial Scaffolding",
  to: "/IndustrialScaffoldingPage"
}, {
  name: "Specialist Access Scaffolding",
  to: "/SpecialistAccessScaffoldingPage"
}, {
  name: "Edge Protection",
  to: "/EdgeProtectionPage"
}, {
  name: "Chimney Scaffolding",
  to: "/ChimneyScaffoldingPage"
}];
function ServicesContent() {
  const {
    posts,
    loading
  } = usePosts();
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.075,
        delayChildren: 0.1
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 18
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="key-services" data-nav="light" className="bg-background text-foreground py-16 md:py-24 lg:py-28 border-b border-border transition-colors">
      <div className="container mx-auto px-5 md:px-8 max-w-7xl">
        {/* Section Header with Industrial Datum Rule */}
        <div className="flex flex-col items-start max-w-3xl mb-12 md:mb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-[3px] bg-primary rounded-lg" />
            <span className="text-xs md:text-sm font-bold tracking-wider text-muted-foreground uppercase">
              Our specialisms
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight font-default text-foreground leading-tight">
            Scaffolding built for safety and scale
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Choose a dedicated team for your commercial site, domestic build, roofing project, or solar installation. Every setup is erected to verified NASC and health-and-safety standards.
          </p>
        </div>

        {/* 4-Column Responsive Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {services.map((service, index) => {
          const matchedPost = posts?.find((p: any) => {
            const name = p.customFields?.photo_name || p.acf?.photo_name || p.title;
            return name && name.trim().toLowerCase() === service.photoName.toLowerCase();
          });
          const dynamicImage = matchedPost?.customFields?.image || (matchedPost as any)?.acf?.image || (matchedPost?.featuredImage && typeof matchedPost.featuredImage === "string" ? matchedPost.featuredImage : null);
          const dynamicAlt = matchedPost?.customFields?.alt_text || (matchedPost as any)?.acf?.alt_text || null;
          const resolvedSrc = (typeof dynamicImage === "object" && dynamicImage?.url ? dynamicImage.url : dynamicImage) || service.imageSrc;
          const resolvedAlt = dynamicAlt || service.alt;
          const resolvedSrcset = dynamicImage ? "" : service.imageSrcset;
          const resolvedSizes = dynamicImage ? "" : service.imageSizes;
          return <motion.div key={service.id} data-index={index} variants={cardVariants} className="h-full flex">
                <Card className="group relative flex flex-col justify-between w-full rounded-lg bg-card border border-border overflow-hidden transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-1 hover:border-primary hover:shadow-md">
                  <div>
                    {/* Aspect Ratio Image Container */}
                    <div className="relative overflow-hidden bg-muted">
                      <AspectRatio ratio={16 / 10}>
                        {loading ? <div className="w-full h-full bg-muted animate-pulse" /> : <img src={resolvedSrc} data-wvc-srcset={resolvedSrcset} data-wvc-sizes={resolvedSizes} alt={resolvedAlt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-105" />}
                      </AspectRatio>
                      {/* Badge tag on image */}
                      <div className="absolute top-3 right-3 bg-secondary/90 text-secondary-foreground backdrop-blur-xs px-2.5 py-1 rounded-sm text-xs font-semibold flex items-center gap-1.5 shadow-2xs">
                        <ShieldCheck className="size-3.5 text-primary" />
                        <span>{service.specTag}</span>
                      </div>
                    </div>

                    <CardHeader className="pt-6 pb-2 px-6">
                      <CardTitle className="text-xl font-bold text-card-foreground group-hover:text-primary transition-colors">
                        {service.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="px-6 py-2">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {service.description}
                      </p>
                    </CardContent>
                  </div>

                  <CardFooter className="px-6 pt-4 pb-6 border-t border-border/50 mt-4 flex items-center justify-between">
                    <Link to={service.to} className="inline-flex items-center gap-2 text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                      <span>View service details</span>
                      <ArrowUpRight className="size-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Compact Specialist Services List & Hub Links */}
        <div className="mt-10 md:mt-12 p-6 md:p-8 rounded-lg bg-card border border-border shadow-xs">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-6 border-b border-border">
            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">
                <Wrench className="size-3.5 text-primary" />
                <span>Specialist Access Capabilities</span>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-foreground">
                Looking for a specific scaffolding configuration?
              </h3>
            </div>
            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <Link to="/ScaffoldingServicesPage" className="inline-flex items-center gap-2 text-xs md:text-sm font-semibold text-primary hover:text-primary/80 transition-colors">
                <span>View All Scaffolding Services</span>
                <ArrowRight className="size-4" />
              </Link>
              <span className="text-border hidden sm:inline">|</span>
              <Link to="/WhoWeWorkWithPage" className="inline-flex items-center gap-2 text-xs md:text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">
                <Users className="size-4" />
                <span>Who We Work With</span>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 pt-6">
            {specialistServices.map((specialist, i) => <Link key={specialist.to} to={specialist.to} data-index={i} className="group flex items-center justify-between p-3 rounded-md bg-muted/40 border border-border/80 hover:border-primary hover:bg-muted/70 transition-[border-color,background-color] duration-150">
                <span className="text-xs md:text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                  {specialist.name}
                </span>
                <ArrowUpRight className="size-3.5 text-muted-foreground group-hover:text-primary transition-transform duration-150 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 shrink-0 ml-2" />
              </Link>)}
          </div>
        </div>

        {/* Operational Standard Callout Strip */}
        <div className="mt-8 md:mt-10 p-6 rounded-lg bg-muted/50 border border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-background border border-border text-primary shrink-0">
              <ShieldCheck className="size-5" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">
                Accredited & Insured Scaffolding Contractors
              </p>
              <p className="text-xs text-muted-foreground">
                Verified compliance with NASC and CHAS across the South East.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 w-full md:w-auto justify-end">
            <Link to="/ContactRequestQuotePage" className="text-xs font-bold uppercase tracking-wider text-primary hover:underline">
              Request a fast quote &rarr;
            </Link>
          </div>
        </div>
      </div>
    </section>;
}
export default function Services() {
  const wp_query = new WP_Query({
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  });
  return <PostsProvider wp_query={{
    post_type: "site_photo",
    per_page: 100,
    status: ["publish"]
  }}>
      <ServicesContent />
    </PostsProvider>;
}